import React from "react";
import {
    VStack,
    Heading,
    Text,
    Box,
    Container,
    Icon,
    Flex,
    Divider,
    Badge,
    Link,
    useColorModeValue,
    UnorderedList,
    ListItem,
    Button,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { MdPrivacyTip } from "react-icons/md";
import {
    FaShieldAlt,
    FaUserLock,
    FaCookie,
    FaThirdWave,
    FaChild,
    FaEnvelope,
    FaHandshake,
    FaExclamationTriangle,
    FaArrowLeft,
} from "react-icons/fa";
import { BiData } from "react-icons/bi";
import { RiPhoneFindLine } from "react-icons/ri";

const Section = ({ icon, title, children, accentColor, cardBg, textColor, borderColor }) => (
    <Box
        w="100%"
        bg={cardBg}
        border="1px solid"
        borderColor={borderColor}
        rounded="xl"
        p={6}
        shadow="sm"
        transition="all 0.2s"
        _hover={{ shadow: "md" }}
    >
        <Flex align="center" mb={4} gap={3}>
            <Flex
                align="center"
                justify="center"
                bg={useColorModeValue("brand.50", "brand.900")}
                p={2}
                rounded="lg"
                color={accentColor}
                flexShrink={0}
            >
                <Icon as={icon} boxSize={5} />
            </Flex>
            <Heading fontSize={{ base: "md", md: "lg" }} fontWeight="700" color={textColor}>
                {title}
            </Heading>
        </Flex>
        <Box color={useColorModeValue("gray.600", "gray.400")} fontSize="md" lineHeight="tall">
            {children}
        </Box>
    </Box>
);

const Privacy = () => {
    const navigate = useNavigate();
    const bg = useColorModeValue("gray.50", "gray.900");
    const cardBg = useColorModeValue("white", "gray.800");
    const textColor = useColorModeValue("gray.800", "white");
    const subTextColor = useColorModeValue("gray.600", "gray.400");
    const accentColor = useColorModeValue("brand.500", "brand.200");
    const borderColor = useColorModeValue("gray.200", "gray.700");
    const warningBg = useColorModeValue("orange.50", "orange.900");
    const warningBorder = useColorModeValue("orange.200", "orange.700");
    const warningText = useColorModeValue("orange.700", "orange.200");

    const sectionProps = { accentColor, cardBg, textColor, borderColor };

    return (
        <>
            <Helmet>
                <title>Privacy Policy | Techy India</title>
                <meta
                    name="description"
                    content="Read the Techyindia Privacy Policy to understand how we collect, use, and protect your personal information."
                />
            </Helmet>

            <VStack
                w="100%"
                minH="100vh"
                bg={bg}
                spacing={0}
                align="stretch"
                py={8}
            >
                <Container maxW="container.lg">
                    <VStack spacing={8} align="center" w="100%">

                        {/* Back Button */}
                        <Flex w="100%" justify="flex-start">
                            <Button
                                leftIcon={<Icon as={FaArrowLeft} />}
                                onClick={() => navigate(-1)}
                                variant="ghost"
                                size="sm"
                                color={accentColor}
                                _hover={{ bg: useColorModeValue("brand.50", "brand.900") }}
                            >
                                Go Back
                            </Button>
                        </Flex>
                        {/* Header */}
                        <VStack spacing={4} textAlign="center">
                            <Flex
                                align="center"
                                justify="center"
                                bg={useColorModeValue("brand.100", "brand.900")}
                                p={4}
                                rounded="full"
                                color="brand.500"
                            >
                                <Icon as={MdPrivacyTip} boxSize={7} />
                            </Flex>
                            <Heading
                                fontSize={{ base: "2xl", md: "4xl" }}
                                fontWeight="800"
                                color={textColor}
                                letterSpacing="-0.5px"
                            >
                                Privacy{" "}
                                <Text as="span" color="brand.500">
                                    Policy
                                </Text>
                            </Heading>
                            <Text color={subTextColor} fontSize="md" maxW="600px">
                                Your privacy is important to us. This policy explains how we collect,
                                use, and protect your information when you use Techyindia.
                            </Text>
                            <Badge colorScheme="brand" fontSize="sm" px={3} py={1} rounded="full">
                                Last Updated: March 15, 2026
                            </Badge>
                        </VStack>

                        <Divider borderColor={borderColor} />

                        {/* Section 1: Information We Collect */}
                        <Section icon={BiData} title="1. Information We Collect" {...sectionProps}>
                            <VStack align="start" spacing={3}>
                                <Box>
                                    <Text fontWeight="600" color={textColor} mb={1}>a. Personal Information</Text>
                                    <Text mb={2} fontSize="sm">
                                        To access Techyindia, users are <Text as="span" fontWeight="700" color={textColor}>required to log in using their email address</Text>. This is the only personal information we collect on the main dashboard.
                                    </Text>
                                    <UnorderedList spacing={1} pl={2}>
                                        <ListItem>Email address (mandatory — used for login and account access)</ListItem>
                                    </UnorderedList>
                                </Box>
                                <Box>
                                    <Text fontWeight="600" color={textColor} mb={1}>b. Usage Data</Text>
                                    <Text mb={2} fontSize="sm">We may collect non-personally-identifiable technical information to improve the app:</Text>
                                    <UnorderedList spacing={1} pl={2}>
                                        <ListItem>App usage activity</ListItem>
                                        <ListItem>Pages visited within the app</ListItem>
                                        <ListItem>Device type and OS version (non-identifiable)</ListItem>
                                    </UnorderedList>
                                </Box>
                                <Box>
                                    <Text fontWeight="600" color={textColor} mb={1}>c. Payment Information</Text>
                                    <UnorderedList spacing={1} pl={2}>
                                        <ListItem>Transaction ID (TXID) submitted by users</ListItem>
                                        <ListItem>Payment verification details</ListItem>
                                    </UnorderedList>
                                    <Box
                                        mt={2}
                                        bg={warningBg}
                                        border="1px solid"
                                        borderColor={warningBorder}
                                        rounded="lg"
                                        p={3}
                                    >
                                        <Text color={warningText} fontSize="sm" fontWeight="600">
                                            ⚠️ We do <Text as="span" textDecoration="underline">not</Text> store your bank details, card details, or any sensitive financial information.
                                        </Text>
                                    </Box>
                                </Box>
                            </VStack>
                        </Section>

                        {/* Section 2: How We Use Your Information */}
                        <Section icon={FaUserLock} title="2. How We Use Your Information" {...sectionProps}>
                            <Text mb={2}>We use your information to:</Text>
                            <UnorderedList spacing={1} pl={2}>
                                <ListItem>Authenticate and provide access to the app via your email</ListItem>
                                <ListItem>Provide and operate our services</ListItem>
                                <ListItem>Verify payments and add balance to user accounts</ListItem>
                                <ListItem>Improve user experience</ListItem>
                                <ListItem>Prevent fraud and misuse</ListItem>
                                <ListItem>Respond to user support requests</ListItem>
                            </UnorderedList>
                        </Section>

                        {/* Section 3: Temporary Phone Numbers */}
                        <Section icon={RiPhoneFindLine} title="3. Temporary Phone Numbers Usage" {...sectionProps}>
                            <Text mb={3}>
                                Techyindia provides temporary virtual phone numbers for:
                            </Text>
                            <UnorderedList spacing={1} pl={2} mb={4}>
                                <ListItem>Testing purposes</ListItem>
                                <ListItem>Development and verification use cases</ListItem>
                            </UnorderedList>
                            <Box
                                bg={warningBg}
                                border="1px solid"
                                borderColor={warningBorder}
                                rounded="lg"
                                p={3}
                            >
                                <Text color={warningText} fontWeight="600" mb={2}>
                                    🚫 Users are strictly prohibited from using our services for:
                                </Text>
                                <UnorderedList spacing={1} pl={2} color={warningText}>
                                    <ListItem>Illegal activities</ListItem>
                                    <ListItem>Fraudulent verification</ListItem>
                                    <ListItem>Misuse of third-party platforms</ListItem>
                                </UnorderedList>
                            </Box>
                            <Text mt={3} fontSize="sm" fontStyle="italic">
                                We reserve the right to suspend accounts violating these terms.
                            </Text>
                        </Section>

                        {/* Section 4: Cookies */}
                        <Section icon={FaCookie} title="4. Cookies and Tracking" {...sectionProps}>
                            <Text mb={2}>We may use cookies or similar technologies to:</Text>
                            <UnorderedList spacing={1} pl={2}>
                                <ListItem>Maintain user sessions</ListItem>
                                <ListItem>Improve performance and user experience</ListItem>
                            </UnorderedList>
                        </Section>

                        {/* Section 5: Third-Party Services */}
                        <Section icon={FaHandshake} title="5. Third-Party Services" {...sectionProps}>
                            <Text mb={2}>We may use third-party services such as:</Text>
                            <UnorderedList spacing={1} pl={2} mb={3}>
                                <ListItem>Google Authentication (for login)</ListItem>
                                <ListItem>Payment providers (external QR-based payments)</ListItem>
                            </UnorderedList>
                            <Text fontSize="sm">
                                These services may collect data according to their own privacy policies.
                            </Text>
                        </Section>

                        {/* Section 6: Data Security */}
                        <Section icon={FaShieldAlt} title="6. Data Security" {...sectionProps}>
                            <Text>
                                We implement reasonable security measures to protect your data.
                                However, no method of transmission over the internet is 100% secure.
                            </Text>
                        </Section>

                        {/* Section 7: Data Sharing */}
                        <Section icon={BiData} title="7. Data Sharing" {...sectionProps}>
                            <Text fontWeight="600" color={textColor} mb={3}>
                                We do <Text as="span" color="green.500">not sell or rent</Text> your personal data.
                            </Text>
                            <Text mb={2}>We may share data only:</Text>
                            <UnorderedList spacing={1} pl={2}>
                                <ListItem>If required by law</ListItem>
                                <ListItem>To prevent fraud or abuse</ListItem>
                                <ListItem>To protect our rights and users</ListItem>
                            </UnorderedList>
                        </Section>

                        {/* Section 8: User Responsibilities */}
                        <Section icon={FaExclamationTriangle} title="8. User Responsibilities" {...sectionProps}>
                            <Text mb={2}>Users are responsible for:</Text>
                            <UnorderedList spacing={1} pl={2}>
                                <ListItem>Providing accurate information</ListItem>
                                <ListItem>Using the service legally</ListItem>
                                <ListItem>Not abusing temporary number services</ListItem>
                            </UnorderedList>
                        </Section>

                        {/* Section 9: Children's Privacy */}
                        <Section icon={FaChild} title="9. Children's Privacy" {...sectionProps}>
                            <Text>
                                Techyindia is{" "}
                                <Text as="span" fontWeight="700" color={textColor}>
                                    not intended for users under the age of 18
                                </Text>
                                .
                            </Text>
                        </Section>

                        {/* Section 10: Changes to Policy */}
                        <Section icon={MdPrivacyTip} title="10. Changes to This Policy" {...sectionProps}>
                            <Text>
                                We may update this Privacy Policy from time to time.
                                Changes will be posted on this page with an updated date.
                            </Text>
                        </Section>

                        {/* Section 11: Contact */}
                        <Section icon={FaEnvelope} title="11. Contact Us" {...sectionProps}>
                            <Text mb={2}>
                                If you have any questions or concerns, please contact us:
                            </Text>
                            <Flex align="center" gap={2}>
                                <Icon as={FaEnvelope} color={accentColor} />
                                <Link
                                    href="mailto:support@techyindia.app"
                                    color={accentColor}
                                    fontWeight="600"
                                    _hover={{ textDecoration: "underline" }}
                                >
                                    support@techyindia.app
                                </Link>
                            </Flex>
                        </Section>

                        {/* Section 12: Consent */}
                        <Box
                            w="100%"
                            bg={useColorModeValue("brand.50", "brand.900")}
                            border="1px solid"
                            borderColor={useColorModeValue("brand.200", "brand.700")}
                            rounded="xl"
                            p={6}
                            textAlign="center"
                        >
                            <Icon as={FaHandshake} boxSize={6} color={accentColor} mb={3} />
                            <Heading fontSize="md" fontWeight="700" color={textColor} mb={2}>
                                12. Consent
                            </Heading>
                            <Text color={subTextColor} fontSize="md">
                                By using Techyindia, you agree to this Privacy Policy and our terms of use.
                            </Text>
                        </Box>

                    </VStack>
                </Container>
            </VStack>
        </>
    );
};

export default Privacy;
